import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { KeyRound, ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-ink-0 text-white relative z-10">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-white mb-8"
            style={{ transitionProperty: "color", transitionDuration: "160ms" }}
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
            Back to sign in
          </Link>

          <div className="flex items-baseline gap-2 mb-8">
            <span className="font-display text-xl font-semibold tracking-tight">EM</span>
            <span className="font-display text-xl font-light tracking-tight text-gold">Operating System</span>
          </div>

          {done ? (
            <div className="em-card p-8 text-center" data-testid="reset-success">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-5">
                <CheckCircle2 className="w-6 h-6 text-emerald-400" strokeWidth={1.75} />
              </div>
              <h1 className="font-display text-2xl text-white mb-2">Password updated</h1>
              <p className="text-sm text-zinc-400">Redirecting you to sign in&hellip;</p>
            </div>
          ) : (
            <>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold mb-3">Reset password</p>
              <h1 className="font-display text-3xl font-light tracking-tight mb-3">Set a new password.</h1>
              <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
                Pick a strong new password &mdash; at least 8 characters. Sign in with your new password on the next screen.
              </p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-2 uppercase tracking-wider">New password</label>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                    <input
                      data-testid="reset-password-input"
                      type="password"
                      autoFocus
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={8}
                      className="em-input pl-10"
                      placeholder="At least 8 characters"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs text-zinc-500 mb-2 uppercase tracking-wider">Confirm password</label>
                  <div className="relative">
                    <KeyRound className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.75} />
                    <input
                      data-testid="reset-password-confirm"
                      type="password"
                      autoComplete="new-password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={8}
                      className="em-input pl-10"
                      placeholder="Repeat your new password"
                    />
                  </div>
                </div>

                <button
                  data-testid="reset-password-submit"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gold hover:bg-gold-hover text-ink-0 font-semibold text-sm rounded-lg px-6 py-3 flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
                >
                  {submitting ? "Saving\u2026" : "Set new password"}
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
